import React, { useEffect } from 'react';
import { LeftOutlined, LoadingOutlined, RightOutlined } from '@ant-design/icons';
import { Alert, Input } from 'antd';
import { CHOOSER_DEFAULTS, objectRecord, firstTruthy, conditionalValue, resolvePageIndicator, handleInlineInputKeyDown, ComposerChooserOption, ComposerChooserOtherInput } from "./ComposerChooserOption.jsx";
export default function ComposerChooser(props) {
  const {
  composerChooser,
  disabled = false,
  loading = false,
  submitting = false,
  chooserRef,
  chooserInlineInputRef,
  handleChooserKeyDown,
  chooserPages,
  normalizedChooserPage,
  chooserTotalPages,
  chooserOptions,
  chooserAllowsOtherInput,
  chooserOtherInputSelected,
  chooserPrimaryActionLabel,
  chooserSelectedKey,
  chooserSelectedKeys,
  chooserSelectionMode,
  chooserRecommendedKey,
  chooserConfirmOnSelect,
  chooserHideSubmit,
  chooserCanSubmit,
  chooserCustomInput,
  setChooserPage,
  setChooserSelectedKey,
  setChooserSelectedKeys,
  setChooserCustomInput,
  setChooserOtherInputSelected,
  handleChooserDismiss,
  handleChooserSubmit,
  chooserSubmitHotkey,
  currentChooserPage,
  } = { ...CHOOSER_DEFAULTS, ...props };
  const chooser = objectRecord(composerChooser);
  const presentationVisible = objectRecord(chooser.presentation).interactive !== false;
  const isBusy = [disabled, loading, submitting].some(Boolean);
  const pendingLabel = submitting ? '提交中' : '处理中';
  const currentPage = objectRecord(currentChooserPage);
  const footer = objectRecord(chooser.footer);
  const title = String(firstTruthy(currentPage.title, chooser.title, '请选择下一步操作')).trim();
  const description = String(currentPage.description ?? '').trim();
  const errorMessage = String(chooser.errorMessage ?? '').trim();

  useEffect(() => {
    if (!presentationVisible) return undefined;
    if (typeof window === 'undefined') {
      return undefined;
    }
    const handleWindowKeyDown = (event) => {
      if (event?.key !== 'Escape') {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      handleChooserDismiss();
    };
    window.addEventListener('keydown', handleWindowKeyDown, true);
    return () => window.removeEventListener('keydown', handleWindowKeyDown, true);
  }, [handleChooserDismiss, presentationVisible]);

  if (!presentationVisible) return null;
  return (
    <div
      className="flare-composer-codex-chooser"
      ref={chooserRef}
      role="listbox"
      aria-busy={[loading, submitting].some(Boolean)}
      tabIndex={0}
      aria-label={String(firstTruthy(chooser.title, '推荐操作'))}
      onKeyDown={handleChooserKeyDown}
    >
      <div className="flare-composer-codex-chooser-head">
        <div className="flare-composer-codex-chooser-title">
          {title}
          {conditionalValue(Boolean(description), (
            <span className="flare-composer-codex-chooser-description">
              {description}
            </span>
          ))}
        </div>
        <div className="flare-composer-codex-chooser-page">
          <span>{resolvePageIndicator(chooser, normalizedChooserPage, chooserTotalPages)}</span>
          {conditionalValue(chooserPages.length > 1, (
            <>
              <button
                className="flare-composer-codex-chooser-page-nav"
                disabled={isBusy || normalizedChooserPage <= 1}
                type="button"
                onClick={() => setChooserPage((prev) => Math.max(1, prev - 1))}
                aria-label="上一页"
              >
                <LeftOutlined />
              </button>
              <button
                className="flare-composer-codex-chooser-page-nav"
                disabled={isBusy || normalizedChooserPage >= chooserPages.length}
                type="button"
                onClick={() => setChooserPage((prev) => Math.min(chooserPages.length, prev + 1))}
                aria-label="下一页"
              >
                <RightOutlined />
              </button>
            </>
          ))}
        </div>
      </div>
      {conditionalValue(Boolean(errorMessage), (
        <Alert message={errorMessage} showIcon type="error" />
      ))}
      <div className="flare-composer-codex-chooser-list">
        {conditionalValue(currentPage.inputKind === 'free_text', (
          <Input
            disabled={isBusy}
            aria-label={title}
            className="flare-composer-codex-chooser-inline-input"
            ref={chooserInlineInputRef}
            value={chooserCustomInput}
            onChange={(event) => setChooserCustomInput(event.target.value)}
            onKeyDown={(event) => handleInlineInputKeyDown({
              event, chooserSubmitHotkey, handleChooserDismiss, handleChooserSubmit,
            })}
          />
        ))}
        {chooserOptions.map((choice, index) => (
          <ComposerChooserOption
            disabled={isBusy}
            choice={choice}
            index={index}
            chooserSelectedKey={chooserSelectedKey}
            chooserSelectedKeys={chooserSelectedKeys}
            chooserSelectionMode={chooserSelectionMode}
            chooserRecommendedKey={chooserRecommendedKey}
            chooserConfirmOnSelect={chooserConfirmOnSelect}
            chooserInlineInputRef={chooserInlineInputRef}
            chooserCustomInput={chooserCustomInput}
            chooserSubmitHotkey={chooserSubmitHotkey}
            setChooserSelectedKey={setChooserSelectedKey}
            setChooserSelectedKeys={setChooserSelectedKeys}
            setChooserCustomInput={setChooserCustomInput}
            handleChooserDismiss={handleChooserDismiss}
            handleChooserSubmit={handleChooserSubmit}
            key={String(objectRecord(choice).key ?? `choice-${index}`)}
          />
        ))}
        <ComposerChooserOtherInput
          disabled={isBusy}
          chooserAllowsOtherInput={chooserAllowsOtherInput}
          chooserOtherInputSelected={chooserOtherInputSelected}
          chooserOptions={chooserOptions}
          chooserInlineInputRef={chooserInlineInputRef}
          chooserCustomInput={chooserCustomInput}
          chooserSubmitHotkey={chooserSubmitHotkey}
          setChooserOtherInputSelected={setChooserOtherInputSelected}
          setChooserCustomInput={setChooserCustomInput}
          handleChooserDismiss={handleChooserDismiss}
          handleChooserSubmit={handleChooserSubmit}
        />
      </div>
      <div className="flare-composer-codex-chooser-actions">
        {conditionalValue(chooser.dismissible !== false, (<button
          className="flare-composer-codex-chooser-dismiss"
          type="button"
          onClick={handleChooserDismiss}
        >
          {String(firstTruthy(footer.dismissLabel, '取消'))}
          <span className="flare-composer-codex-chooser-esc">Esc</span>
        </button>))}
        {conditionalValue(!chooserHideSubmit, (
          <button
            className="flare-composer-codex-chooser-submit"
            disabled={isBusy || !chooserCanSubmit}
            aria-busy={[loading, submitting].some(Boolean)}
            type="button"
            onClick={() => handleChooserSubmit()}
          >
            {conditionalValue([loading, submitting].some(Boolean), <LoadingOutlined />)}
            {conditionalValue([loading, submitting].some(Boolean), pendingLabel,
              String(firstTruthy(chooserPrimaryActionLabel, footer.submitLabel, '提交')))}
            {conditionalValue(![loading, submitting].some(Boolean), (
              <span className="flare-composer-codex-chooser-enter">Enter</span>
            ))}
          </button>
        ))}
      </div>
    </div>
  );
}
